/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useSelector } from 'react-redux';
import { useRef, useEffect } from 'react';
import Conversation from './Conversation';
import ToolBar from './ToolBar';
import CameraArea from './CameraArea';
import AudioController from './AudioController';
import { isMobile } from '@/utils/utils';
import style from './index.module.less';
import AiAvatarCard from '@/components/AiAvatarCard';
import { RootState } from '@/store';
import UserTag from '@/components/UserTag';
import FullScreenCard from '@/components/FullScreenCard';
import MobileToolBar from '@/pages/Mobile/MobileToolBar';
import { useScene } from '@/lib/useCommon';


function Room() {
  const room = useSelector((state: RootState) => state.room);
  const { isShowSubtitle, scene, isFullScreen } = room;
  const { isAvatarScene } = useScene();
  // 使用 useRef 保存 wakeLock 引用
  const wakeLockRef = useRef<any>(null);

  useEffect(() => {
    // 请求屏幕常亮
    const requestWakeLock = async () => {
      try {
        // ✅ 检查页面是否可见
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          // ✅ 如果已有 wakeLock，先释放（避免重复）
          if (wakeLockRef.current) {
            await wakeLockRef.current.release();
          }
          
          const wakeLock = await (navigator as any).wakeLock.request('screen');
          wakeLockRef.current = wakeLock;
          console.log('✅ 屏幕常亮已激活');

          wakeLock.addEventListener('release', () => {
            console.log('⚠️ 屏幕常亮已被释放');
            wakeLockRef.current = null;
          });
        }
      } catch (err) {
        console.error('❌ 启用屏幕常亮失败:', err);
      }
    };

    // ✅ 关键：处理页面可见性变化
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 用户返回页面，重新激活屏幕常亮');
        await requestWakeLock();
      } else {
        console.log('👋 用户离开页面');
      }
    };

    // 初始激活
    requestWakeLock();

    // ✅ 添加监听器
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 清理函数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch((err: any) => {
          console.error('释放屏幕常亮失败:', err);
        });
      }
    };
  }, []);

  return (
    <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
      {isMobile() ? <div className={style.mobilePlayer} id="mobile-local-player" /> : null}
      {isMobile() ? <MobileToolBar /> : null}
      {isShowSubtitle && !isMobile() ? (
        <UserTag name={scene} className={style.subTitleUserTag} />
      ) : null}
      {(isFullScreen || isAvatarScene) && !isMobile() ? (
        <FullScreenCard />
      ) : isMobile() && isShowSubtitle ? null : (
        <AiAvatarCard
          showUserTag={!isShowSubtitle}
          showStatus={!isShowSubtitle}
          className={isShowSubtitle ? style.subtitleAiAvatar : ''}
        />
      )}
      {isMobile() ? null : <CameraArea />}
      <Conversation className={style.conversation} showSubtitle={isShowSubtitle} />
      <ToolBar className={style.toolBar} />
      <AudioController className={style.controller} />
      <div className={style.declare}>AI生成内容由大模型生成，不能完全保障真实</div>
    </div>
  );
}

export default Room;
